import React, { useState, useEffect } from "react";
import { commandService } from "../../services/api";
import { Command } from "../../types";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";

const Commands: React.FC = () => {
    const { user } = useAuth();
    const [commands, setCommands] = useState<Command[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [deletingId, setDeletingId] = useState<number | null>(null);

    useEffect(() => {
        if (user?.username) loadCommands();
    }, [user]);

    const loadCommands = async () => {
        try {
            setIsLoading(true);
            const data = await commandService.getByUsername(user!.username);
            setCommands(data || []);
        } catch {
            setError("Erreur lors du chargement des commandes");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        setDeletingId(id);
        try {
            await commandService.delete(id);
            setCommands((prev) => prev.filter((c) => c.id !== id));
            setSuccess("Commande supprimée");
            setTimeout(() => setSuccess(""), 3000);
        } catch {
            setError("Erreur lors de la suppression");
        } finally {
            setDeletingId(null);
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="max-w-4xl mx-auto p-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Mes commandes</h1>

            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>
            )}
            {success && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">{success}</div>
            )}

            {commands.length === 0 ? (
                <div className="text-center py-12 text-gray-500">Aucune commande pour le moment</div>
            ) : (
                <div className="space-y-4">
                    {commands.map((command) => (
                        <div key={command.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-sm text-gray-500">Commande #{command.id}</p>
                                    <p className="text-lg font-semibold text-gray-800 mt-1">
                                        Total : {command.total_price.toFixed(2)} €
                                    </p>
                                    {command.product && command.product.length > 0 && (
                                        <ul className="mt-2 space-y-1">
                                            {command.product.map((p, i) => (
                                                <li key={p.id} className="text-sm text-gray-600">
                                                    {p.name}
                                                    {command.product_price?.[i] !== undefined && (
                                                        <span className="ml-2 text-gray-400">
                                                            — {command.product_price[i].toFixed(2)} €
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleDelete(command.id)}
                                    disabled={deletingId === command.id}
                                    className="ml-4 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                                >
                                    {deletingId === command.id ? "..." : "Supprimer"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Commands;
